import type { ResolveResponse } from '@km3d/shared';
import { DEFAULT_CAMERA_ORBIT } from '@km3d/shared/constants';
import { chooseVariant, isExpired, reasonFromStatus } from './choose.ts';
import { DIMENSIONS_STYLE, Dimensions, type DimensionsViewer } from './dimensions.ts';

declare const __MV_FILE__: string;

type EventType = 'view' | 'interact' | 'ar_open' | 'error' | 'badge_click';

/** Minimalan deo model-viewer API-ja koji koristimo. */
interface ModelViewer extends HTMLElement {
  src: string | null;
  canActivateAR: boolean;
  activateAR(): Promise<void>;
}

// NE `new URL('./', import.meta.url)`: Vite taj oblik prepisuje kao referencu na asset
// (ubaci data: URL), pa origin ispadne "null". Direktorijum skripte se računa iz stringa.
const SCRIPT_URL: string = import.meta.url;
const HERE = new URL(SCRIPT_URL.slice(0, SCRIPT_URL.lastIndexOf('/') + 1));
const DEFAULT_API = HERE.origin;

let mvPromise: Promise<unknown> | null = null;

/** model-viewer se učitava jednom po stranici, sa našeg CDN-a, pinovana verzija (PLAN §7). */
function loadModelViewer(): Promise<unknown> {
  if (!mvPromise) {
    const g = globalThis as unknown as { ModelViewerElement?: Record<string, string> };
    // Čita se pri inicijalizaciji model-viewer-a; bez ovoga ide na gstatic.
    g.ModelViewerElement = {
      ...g.ModelViewerElement,
      meshoptDecoderLocation: new URL('decoders/meshopt_decoder.js', HERE).href,
      ktx2TranscoderLocation: new URL('decoders/basis/', HERE).href,
    };
    mvPromise = import(/* @vite-ignore */ new URL(__MV_FILE__, HERE).href);
  }
  return mvPromise;
}

// !important na skrivanju: CSS prodavnice (km-3d{display:block}) inače pobedi :host pravilo.
const STYLE =
  `
:host{display:block;position:relative;overflow:hidden;aspect-ratio:var(--km3d-aspect,1/1);
background:var(--km3d-bg,transparent);border-radius:var(--km3d-radius,0);contain:content}
:host([hidden]),:host([data-state=unavailable]){display:none!important}
img,model-viewer{position:absolute;inset:0;width:100%;height:100%}
img{object-fit:contain}
::slotted([slot=poster]){position:absolute!important;inset:0;width:100%!important;height:100%!important;object-fit:contain}
:host([data-state=ready]) ::slotted([slot=poster]){visibility:hidden}
model-viewer{--poster-color:transparent;background:transparent}
model-viewer:focus{outline:none}
model-viewer:focus-visible{outline:2px solid #2563eb;outline-offset:-2px}
.btn{position:absolute;left:50%;bottom:12px;transform:translateX(-50%);font:600 14px/1 system-ui,sans-serif;
padding:10px 16px;border-radius:999px;border:0;background:rgba(15,23,42,.85);color:#fff;cursor:pointer}
.btn:focus-visible{outline:2px solid #fff;outline-offset:2px}
.ar{bottom:auto;top:12px;left:auto;right:12px;transform:none}
.badge{position:absolute;z-index:2;right:10px;bottom:10px;display:inline-flex;align-items:center;gap:6px;
font:600 11px/1 system-ui,-apple-system,sans-serif;letter-spacing:.01em;padding:5px 9px 5px 6px;
border-radius:999px;background:rgba(15,23,42,.75);color:#fff;text-decoration:none;
opacity:.85;transition:opacity .15s}
.badge:hover{opacity:1}
.badge:focus-visible{outline:2px solid #fff;outline-offset:2px;opacity:1}
.badge svg{width:14px;height:14px;flex:none;border-radius:3px}
:host(:not([data-state=ready])) .badge{display:none}
` + DIMENSIONS_STYLE;

/** Znak ZWE (api/zwe-brand, favicon-small.svg): bordo kvadrat sa belim slovom. */
const ZWE_MARK =
  '<svg viewBox="0 0 32 32" aria-hidden="true"><rect width="32" height="32" rx="5" fill="#8F274A"/>' +
  '<path fill="#fff" transform="translate(3.2 7.27) scale(.19845)" d="M84 12L94 28L49 68L117 68L129 88L0 88Z"/></svg>';

/**
 * Potpis „3D powered by ZWE" dole desno, vidljiv tek kad je model spreman (CSS po `data-state`).
 * Jedan element iz konstruktora — `#reset()` ga ne dira, pa promena SKU-a ne pravi duplikate.
 * `nofollow`: link iz widgeta Google tretira kao šemu linkova; ovo je reklama, ne link building.
 */
function badge(): HTMLAnchorElement {
  const a = document.createElement('a');
  a.className = 'badge';
  a.setAttribute('part', 'badge');
  const shop = typeof location === 'undefined' ? '' : location.hostname;
  a.href = `https://zwe.rs/?utm_source=km3d&utm_medium=badge&utm_campaign=${encodeURIComponent(shop)}`;
  a.target = '_blank';
  a.rel = 'noopener nofollow';
  a.title = '3D modeli za webshop — ZWE';
  a.innerHTML = ZWE_MARK;
  a.append('3D powered by ZWE');
  return a;
}

/**
 * Ključ iz `key` ili `publishable-key`. React (i Lovable) `key` tretira kao svoj rezervisani
 * prop i nikad ga ne prosledi elementu, pa se tamo koristi `publishable-key`.
 */
export function publishableKey(el: Element): string | null {
  return el.getAttribute('key') ?? el.getAttribute('publishable-key');
}

/** iPhone/iPad (i iPadOS koji se predstavlja kao Mac). */
function isIOS(): boolean {
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) || (ua.includes('Macintosh') && navigator.maxTouchPoints > 1);
}

/** Čeka da se poster učita (najduže 3 s), pa da browser bude besposlen. */
async function afterPoster(img: HTMLImageElement): Promise<void> {
  if (!img.complete) {
    await new Promise<void>((resolve) => {
      const done = () => resolve();
      img.addEventListener('load', done, { once: true });
      img.addEventListener('error', done, { once: true });
      setTimeout(done, 3000);
    });
  }
  await new Promise<void>((resolve) => {
    if ('requestIdleCallback' in window) requestIdleCallback(() => resolve(), { timeout: 1000 });
    else setTimeout(resolve, 50);
  });
}

export class Km3dElement extends HTMLElement {
  static observedAttributes = ['sku', 'key', 'publishable-key', 'material-variant', 'dimensions'];

  #root: ShadowRoot;
  #io: IntersectionObserver | null = null;
  #data: ResolveResponse | null = null;
  #mv: ModelViewer | null = null;
  #started = false;
  #retried = false;
  #ready = false;
  #dims: Dimensions | null = null;

  constructor() {
    super();
    this.#root = this.attachShadow({ mode: 'open' });
    const style = document.createElement('style');
    style.textContent = STYLE;
    // <img slot="poster"> iz HTML-a prodavnice: browser ga nalazi bez JS-a (LCP), mi ga samo pozicioniramo.
    const slot = document.createElement('slot');
    slot.name = 'poster';
    const potpis = badge();
    // Klik na potpis = beacon (link je _blank, pa strana ostaje i sendBeacon stigne).
    potpis.addEventListener('click', () => this.#beacon('badge_click'));
    this.#root.append(style, slot, potpis);
    if (this.hasAttribute('background')) {
      this.style.setProperty('--km3d-bg', this.getAttribute('background'));
    }
  }

  get #api(): string {
    return (this.getAttribute('api') ?? DEFAULT_API).replace(/\/$/, '');
  }

  connectedCallback(): void {
    if (this.#started) return;
    if (this.getAttribute('loading') === 'eager' || typeof IntersectionObserver === 'undefined') {
      void this.#start();
      return;
    }
    this.#io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          this.#io?.disconnect();
          void this.#start();
        }
      },
      { rootMargin: '200px' },
    );
    this.#io.observe(this);
  }

  disconnectedCallback(): void {
    this.#io?.disconnect();
  }

  attributeChangedCallback(name: string, old: string | null, value: string | null): void {
    // Boja/završna obrada: bez ponovnog učitavanja, model-viewer samo zameni materijale.
    if (name === 'material-variant') {
      this.#applyVariant();
      return;
    }
    if (name === 'dimensions') {
      this.#setupDimensions();
      return;
    }
    // Promena SKU-a na već pokrenutom elementu (npr. izbor varijante proizvoda na strani).
    if (this.#started && old !== null && old !== value) {
      this.#reset();
      void this.#start();
    }
  }

  /** KHR_materials_variants iz modela (prazno dok resolve ne stigne ili kad model nema varijanti). */
  get materialVariants(): string[] {
    return this.#data?.materialVariants ?? [];
  }

  /** Izbor boje/završne obrade; `null` = osnovni materijali modela. */
  setMaterialVariant(name: string | null): void {
    if (name === null) this.removeAttribute('material-variant');
    else this.setAttribute('material-variant', name);
  }

  /** Kotiranje: atribut `dimensions` daje dugme, `dimensions="show"` i odmah prikazuje kote. */
  #setupDimensions(): void {
    const attr = this.getAttribute('dimensions');
    if (attr === null) {
      this.#dims?.toggle(false);
      this.#dims?.button.remove();
      return;
    }
    if (!this.#dims) {
      this.#dims = new Dimensions(this.#root, (shown) =>
        this.dispatchEvent(
          new CustomEvent('km3d:dimensions', { bubbles: true, composed: true, detail: { shown } }),
        ),
      );
    } else if (!this.#dims.button.isConnected) {
      this.#root.append(this.#dims.button);
    }
    // Iz #onLoad ili kad prodavnica doda atribut posle učitavanja; ponovni poziv ne radi ništa.
    if (this.#ready && this.#mv) this.#dims.attach(this.#mv as unknown as DimensionsViewer);
    if (attr === 'show') this.#dims.toggle(true);
  }

  /** Prikaz/skrivanje kota iz koda prodavnice (traži atribut `dimensions`). */
  showDimensions(show = true): void {
    this.#dims?.toggle(show);
  }

  #applyVariant(): void {
    const mv = this.#mv;
    if (!mv) return;
    const wanted = this.getAttribute('material-variant');
    // Nepoznat naziv se ignoriše (osnovni materijali), da greška u CMS-u ne sakrije model.
    if (wanted && this.materialVariants.includes(wanted)) mv.setAttribute('variant-name', wanted);
    else mv.removeAttribute('variant-name');
  }

  #reset(): void {
    this.#dims?.detach();
    this.#mv?.remove();
    this.#root.querySelectorAll('img,.btn').forEach((n) => n.remove());
    this.#mv = null;
    this.#data = null;
    this.#retried = false;
    this.#ready = false;
    this.removeAttribute('data-state');
  }

  async #resolve(): Promise<ResolveResponse | null> {
    const sku = this.getAttribute('sku');
    const key = publishableKey(this);
    if (!sku || !key) {
      this.#unavailable('missing_attributes');
      return null;
    }
    let res: Response;
    try {
      res = await fetch(
        `${this.#api}/v1/resolve?sku=${encodeURIComponent(sku)}&key=${encodeURIComponent(key)}`,
        { credentials: 'omit' },
      );
    } catch {
      this.#unavailable('network');
      return null;
    }
    if (!res.ok) {
      let error: string | undefined;
      try {
        error = ((await res.json()) as { error?: string }).error;
      } catch {
        /* telo nije JSON */
      }
      this.#unavailable(reasonFromStatus(res.status, error));
      return null;
    }
    this.#data = (await res.json()) as ResolveResponse;
    return this.#data;
  }

  async #start(): Promise<void> {
    this.#started = true;
    this.setAttribute('data-state', 'loading');
    const data = await this.#resolve();
    if (!data) return;

    let poster = this.querySelector<HTMLImageElement>(':scope > img[slot="poster"]');
    if (!poster) {
      poster = document.createElement('img');
      poster.src = data.poster;
      poster.srcset = `${data.poster} 1x, ${data.poster2x} 2x`;
      poster.alt = data.name;
      poster.fetchPriority = 'high';
      poster.setAttribute('part', 'poster');
      this.#root.append(poster);
    }
    this.setAttribute('data-state', 'poster');
    // model-viewer (~1 MB) kreće tek kad je poster iscrtan: inače mu otima protok i kvari LCP.
    await afterPoster(poster);

    if (this.hasAttribute('poster-only')) {
      const btn = this.#button('Prikaži u 3D', 'btn');
      btn.addEventListener('click', () => {
        btn.remove();
        void this.#loadModel();
      });
      return;
    }
    void this.#loadModel();
  }

  #button(label: string, cls: string): HTMLButtonElement {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = cls;
    b.textContent = label;
    b.setAttribute('part', cls === 'btn' ? 'button' : 'ar-button');
    this.#root.append(b);
    return b;
  }

  #variantUrl(data: ResolveResponse): string {
    const conn = (
      navigator as Navigator & { connection?: { saveData?: boolean; effectiveType?: string } }
    ).connection;
    const v = chooseVariant(
      {
        innerWidth: window.innerWidth,
        saveData: conn?.saveData,
        effectiveType: conn?.effectiveType,
      },
      this.getAttribute('variant'),
    );
    return data.variants[v];
  }

  async #loadModel(): Promise<void> {
    let data = this.#data;
    if (!data) return;
    // Tab ostavljen otvoren: token istekao pre nego što je korisnik kliknuo (PLAN §7 korak 11).
    if (isExpired(data.expiresAt, Date.now())) {
      data = await this.#resolve();
      if (!data) return;
    }
    try {
      await loadModelViewer();
    } catch {
      this.#unavailable('viewer_load_failed');
      return;
    }

    const mv = document.createElement('model-viewer') as ModelViewer;
    mv.setAttribute('src', this.#variantUrl(data));
    mv.setAttribute('alt', data.name);
    mv.setAttribute('camera-controls', '');
    mv.setAttribute('touch-action', 'pan-y');
    mv.setAttribute('interaction-prompt', 'none');
    mv.setAttribute('camera-orbit', this.getAttribute('camera-orbit') ?? DEFAULT_CAMERA_ORBIT);
    // Podrazumevano model-viewer ne pušta udaljavanje dalje od početnog kadra (sanimarket 27.09).
    mv.setAttribute('min-camera-orbit', 'auto auto 5%');
    mv.setAttribute('max-camera-orbit', 'auto auto 300%');
    const variant = this.getAttribute('material-variant');
    if (variant && data.materialVariants.includes(variant))
      mv.setAttribute('variant-name', variant);
    mv.setAttribute('shadow-intensity', '1');
    mv.setAttribute('environment-image', 'neutral');
    mv.setAttribute('loading', 'eager');
    mv.setAttribute('part', 'viewer');
    if (this.hasAttribute('ar')) {
      mv.setAttribute('ar', '');
      // Scene Viewer pre WebXR-a: bolje prati površinu i pokazuje „100 %" (proba na Androidu
      // 27.09: WebXR na tamnom stolu je pogrešno procenio daljinu). Proizvod je uvek u pravoj
      // veličini — zum prstima u AR-u bi kupcu pokazao pogrešne mere.
      mv.setAttribute('ar-modes', 'scene-viewer webxr quick-look');
      mv.setAttribute('ar-scale', 'fixed');
      // Unapred napravljen USDZ: iPhone ne konvertuje GLB pri svakom tapu (sporo i ume da pukne).
      if (data.variants.usdz) mv.setAttribute('ios-src', data.variants.usdz);
      if (data.arPlacement === 'wall') mv.setAttribute('ar-placement', 'wall');
    }
    mv.style.opacity = '0';
    // Poster već pokazuje da se učitava; podrazumevana traka model-viewer-a bi stajala preko njega.
    const bar = document.createElement('div');
    bar.slot = 'progress-bar';
    // Ugrađeno AR dugme model-viewer-a bi stajalo pored našeg (#activateAR bira ar/usdz varijantu).
    const noAr = document.createElement('span');
    noAr.slot = 'ar-button';
    noAr.style.display = 'none';
    mv.append(bar, noAr);
    mv.addEventListener('load', () => this.#onLoad(mv), { once: false });
    mv.addEventListener('error', () => void this.#onError(mv));
    mv.addEventListener('camera-change', (e) => {
      if ((e as CustomEvent<{ source?: string }>).detail?.source === 'user-interaction') {
        this.#beaconOnce('interact');
      }
    });
    this.#mv = mv;
    this.#root.append(mv);
  }

  #onLoad(mv: ModelViewer): void {
    if (this.#ready) return;
    this.#ready = true;
    mv.style.transition = 'opacity .3s';
    mv.style.opacity = '1';
    this.#root.querySelector('img')?.remove();
    this.setAttribute('data-state', 'ready');
    this.#setupDimensions();
    this.#beacon('view');
    this.dispatchEvent(
      new CustomEvent('km3d:ready', {
        bubbles: true,
        composed: true,
        detail: {
          sku: this.getAttribute('sku'),
          modelId: this.#data?.modelId,
          materialVariants: this.materialVariants,
          note: this.#data?.note ?? null,
        },
      }),
    );
    if (this.hasAttribute('ar') && mv.canActivateAR) {
      const b = this.#button('AR', 'btn ar');
      b.setAttribute('aria-label', 'Pogledaj u svom prostoru (AR)');
      b.addEventListener('click', () => void this.#activateAR(mv));
    }
  }

  async #activateAR(mv: ModelViewer): Promise<void> {
    this.#beacon('ar_open');
    const arUrl = this.#data?.variants.ar;
    // iOS sa gotovim USDZ-om: Quick Look ga otvara direktno, učitavanje ar.glb bi samo odložilo
    // otvaranje (i potrošilo "user gesture" pre activateAR).
    const iosSrc = mv.getAttribute('ios-src');
    // Gotov USDZ nosi osnovne materijale; za izabranu boju model-viewer pravi USDZ iz trenutne
    // scene (sporije, ali tačna boja).
    if (isIOS() && iosSrc && mv.getAttribute('variant-name')) {
      mv.removeAttribute('ios-src');
      try {
        await mv.activateAR();
      } finally {
        mv.setAttribute('ios-src', iosSrc);
      }
      return;
    }
    const quickLookReady = isIOS() && !!iosSrc;
    if (arUrl && !quickLookReady && mv.getAttribute('src') !== arUrl) {
      // Scene Viewer / WebXR koriste ar varijantu (PLAN §7 korak 6).
      mv.setAttribute('src', arUrl);
      // Android: Scene Viewer sam preuzima fajl sa adrese, a Chrome pušta otvaranje samo par
      // sekundi posle dodira — čekanje da se ar.glb učita u stranici bi na sporoj vezi tiho
      // progutalo AR. Zato odmah. WebXR (drugde) traži učitan model, pa se tu čeka.
      if (!/Android/i.test(navigator.userAgent)) {
        await new Promise<void>((r) => mv.addEventListener('load', () => r(), { once: true }));
      }
    }
    await mv.activateAR();
  }

  async #onError(mv: ModelViewer): Promise<void> {
    // Verovatno istekao token: ponovi resolve jednom (PLAN §7 korak 11).
    if (!this.#retried) {
      this.#retried = true;
      const data = await this.#resolve();
      if (data) {
        // U istom satu resolve vrati ISTI URL (token je zaokružen na sat), a isti `src` ne
        // pokreće novo učitavanje. Fragment menja atribut, a ne ide u zahtev ni u keš ključ.
        const url = this.#variantUrl(data);
        mv.setAttribute('src', mv.getAttribute('src') === url ? `${url}#retry` : url);
      }
      return;
    }
    this.#beacon('error');
    this.#unavailable('load_error');
  }

  #unavailable(reason: string): void {
    this.setAttribute('data-state', 'unavailable');
    this.dispatchEvent(
      new CustomEvent('km3d:unavailable', {
        bubbles: true,
        composed: true,
        detail: { sku: this.getAttribute('sku'), reason },
      }),
    );
  }

  #beaconOnce(type: EventType): void {
    const id = this.#data?.modelId;
    if (!id) return;
    const k = `km3d:${type}:${id}`;
    try {
      if (sessionStorage.getItem(k)) return;
      sessionStorage.setItem(k, '1');
    } catch {
      /* storage blokiran — šaljemo i dalje, ali samo jednom po elementu */
      if (this.dataset[type]) return;
      this.dataset[type] = '1';
    }
    this.#beacon(type);
  }

  #beacon(type: EventType): void {
    const modelId = this.#data?.modelId;
    const key = publishableKey(this);
    if (!modelId || !key) return;
    // String telo = text/plain, pa beacon ne traži CORS preflight.
    const body = JSON.stringify({ key, modelId, type, ts: Date.now() });
    try {
      if (navigator.sendBeacon?.(`${this.#api}/v1/events`, body)) return;
    } catch {
      /* pada na fetch */
    }
    void fetch(`${this.#api}/v1/events`, {
      method: 'POST',
      body,
      keepalive: true,
      credentials: 'omit',
    }).catch(() => undefined);
  }
}
